/**
 * 🔒 CoachBookingSharedContext — lecture seule côté coach humain
 *
 * Affiche la synthèse Owen SI et seulement SI un consentement actif existe
 * pour ce booking. Messages explicites si aucun/expiré/révoqué/pending.
 * Aucune modification possible. Aucune donnée raw. Synthèse IA uniquement.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Shield, Clock, XCircle, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { getApiUrl } from '../../config/environment';
import { useAuth } from '../../contexts/AuthContext';

const T = {
  bg: '#FBF9F3', card: '#FFFFFF', ink: '#1C1147', text: '#1F2937',
  muted: '#6B7280', border: '#E8E2D2', gold: '#D4AF37', goldDeep: '#B7901F',
  ivoryDeep: '#FFFCEC', green: '#48825A', red: '#9B2C1F', amber: '#B7901F',
};

const SCOPE_LABEL = {
  A: 'Synthèse courte',
  B: 'Synthèse + objectifs + critères',
  C: 'Synthèse + rapports IA',
  D: 'Personnalisé',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('fr-FR'); } catch { return iso.slice(0,10); }
};

function Block({ title, children, testId }) {
  return (
    <div data-testid={testId}>
      <div
        className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-1"
        style={{ color: T.goldDeep }}
      >
        {title}
      </div>
      <div style={{ color: T.text, fontSize: 14, lineHeight: 1.55 }}>{children}</div>
    </div>
  );
}

function InfoBanner({ tone, icon: Icon, title, message, testId }) {
  const palette = {
    muted: { bg: T.ivoryDeep, border: T.border, color: T.ink, iconColor: T.muted },
    amber: { bg: '#FFF8E0', border: '#F3D060', color: T.amber, iconColor: T.amber },
    red:   { bg: '#FFF4F2', border: '#F8C9C0', color: T.red, iconColor: T.red },
  }[tone] || { bg: T.ivoryDeep, border: T.border, color: T.ink, iconColor: T.muted };

  return (
    <div
      data-testid={testId}
      className="p-4 rounded-xl flex items-start gap-3"
      style={{ background: palette.bg, border: `1px solid ${palette.border}` }}
    >
      {Icon && <Icon className="w-5 h-5 shrink-0 mt-0.5" style={{ color: palette.iconColor }} />}
      <div>
        <div
          className="text-sm font-semibold mb-0.5"
          style={{ color: palette.color, fontFamily: 'Georgia, serif' }}
        >
          {title}
        </div>
        <div style={{ color: T.muted, fontSize: 12.5 }}>{message}</div>
      </div>
    </div>
  );
}

export default function CoachBookingSharedContext({ bookingId }) {
  const { token } = useAuth();
  const API = getApiUrl();
  const [state, setState] = useState('loading'); // loading | no_share | expired | pending | active | error
  const [share, setShare] = useState(null);
  const [summary, setSummary] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const fetchData = useCallback(async () => {
    if (!bookingId || !token) return;
    setState('loading');
    try {
      const r = await fetch(`${API}/coach/bookings/${bookingId}/shared-context`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      const j = await r.json();
      setShare(j.share || null);
      setSummary(j.summary || null);
      setState(j.status || 'no_share');
    } catch (e) {
      setErrorMsg(e.message);
      setState('error');
    }
  }, [API, bookingId, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const Wrapper = ({ children }) => (
    <div
      data-testid="coach-shared-context"
      className="rounded-2xl p-5 space-y-4"
      style={{ background: T.bg, border: `1px solid ${T.border}` }}
    >
      <div className="flex items-center gap-2">
        <Shield className="w-4 h-4" style={{ color: T.goldDeep }} />
        <span
          className="text-[11px] uppercase tracking-[0.22em] font-semibold"
          style={{ color: T.goldDeep }}
        >
          Contexte Owen partagé · Lecture seule
        </span>
      </div>
      {children}
      <p className="text-[10px] mt-2" style={{ color: T.muted }}>
        Synthèse générée par l'IA d'après le consentement explicite du coaché. Conformité RGPD.
      </p>
    </div>
  );

  if (state === 'loading') {
    return (
      <Wrapper>
        <div
          data-testid="coach-shared-context-loading"
          className="flex items-center gap-2 text-sm py-6"
          style={{ color: T.muted }}
        >
          <Loader2 className="w-4 h-4 animate-spin" />
          Chargement du contexte partagé…
        </div>
      </Wrapper>
    );
  }

  if (state === 'error') {
    return (
      <Wrapper>
        <InfoBanner
          tone="red"
          icon={AlertCircle}
          title="Impossible de charger le contexte"
          message={errorMsg || 'Veuillez réessayer plus tard.'}
          testId="coach-shared-context-error"
        />
      </Wrapper>
    );
  }

  if (state === 'no_share') {
    return (
      <Wrapper>
        <InfoBanner
          tone="muted"
          icon={Shield}
          title="Aucun partage autorisé"
          message="L'utilisateur n'a pas autorisé le partage de son contexte Owen. Vous découvrirez son parcours en séance."
          testId="coach-shared-context-no-share"
        />
      </Wrapper>
    );
  }

  if (state === 'expired') {
    return (
      <Wrapper>
        <InfoBanner
          tone="muted"
          icon={Clock}
          title="Partage expiré"
          message="Le partage de contexte n'est plus disponible (période de rétention écoulée)."
          testId="coach-shared-context-expired"
        />
      </Wrapper>
    );
  }

  if (share && share.status === 'revoked') {
    return (
      <Wrapper>
        <InfoBanner
          tone="red"
          icon={XCircle}
          title="Partage révoqué"
          message="L'utilisateur a révoqué son consentement. L'accès à la synthèse n'est plus possible."
          testId="coach-shared-context-revoked"
        />
      </Wrapper>
    );
  }

  if (state === 'pending' || (share && !summary)) {
    return (
      <Wrapper>
        <InfoBanner
          tone="amber"
          icon={Loader2}
          title="Synthèse en cours de génération"
          message="L'utilisateur a autorisé le partage. La synthèse sera disponible dans quelques instants."
          testId="coach-shared-context-pending"
        />
      </Wrapper>
    );
  }

  // status === 'active' && summary present
  const s = summary?.synthesis || {};
  return (
    <Wrapper>
      <div className="flex items-center gap-2 flex-wrap">
        <CheckCircle2 className="w-4 h-4" style={{ color: T.green }} />
        <span
          className="px-2 py-0.5 rounded-md text-[10px] font-semibold"
          style={{ background: T.ivoryDeep, color: T.goldDeep, border: `1px solid ${T.border}` }}
        >
          Scope {share?.consent_scope} · {SCOPE_LABEL[share?.consent_scope] || '—'}
        </span>
        <span className="text-[11px]" style={{ color: T.muted }}>
          Expire le {fmtDate(share?.expires_at)}
        </span>
      </div>

      <div
        className="p-4 rounded-xl space-y-3"
        style={{ background: T.card, border: `1px solid ${T.border}` }}
        data-testid="coach-shared-context-synthesis"
      >
        {s.contexte_parcours && (
          <Block title="Contexte du parcours" testId="coach-shared-block-contexte">
            {s.contexte_parcours}
          </Block>
        )}
        {Array.isArray(s.objectifs_travailles) && s.objectifs_travailles.length > 0 && (
          <Block title="Objectifs travaillés" testId="coach-shared-block-objectifs">
            <ul className="list-disc pl-4 space-y-0.5">
              {s.objectifs_travailles.map((o, i) => <li key={i}>{o}</li>)}
            </ul>
          </Block>
        )}
        {Array.isArray(s.preuves_observees) && s.preuves_observees.length > 0 && (
          <Block title="Preuves observées" testId="coach-shared-block-preuves">
            <ul className="list-disc pl-4 space-y-0.5">
              {s.preuves_observees.map((o, i) => <li key={i}>{o}</li>)}
            </ul>
          </Block>
        )}
        {Array.isArray(s.points_vigilance) && s.points_vigilance.length > 0 && (
          <Block title="Points de vigilance" testId="coach-shared-block-vigilance">
            <ul className="list-disc pl-4 space-y-0.5">
              {s.points_vigilance.map((o, i) => <li key={i}>{o}</li>)}
            </ul>
          </Block>
        )}
        {s.action_progres && (
          <Block title="Action de progrès" testId="coach-shared-block-action">
            {s.action_progres}
          </Block>
        )}
        {s.recommandation_coach && (
          <Block title="Recommandation coach" testId="coach-shared-block-reco">
            {s.recommandation_coach}
          </Block>
        )}
      </div>
    </Wrapper>
  );
}
